import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import type { LifecyclePage } from "./use-identity.ts";
import { lifecycleActionLabel, actorTypeLabel, lifecycleActionHref } from "./vocab.ts";

/**
 * The lifecycle timeline region (task 11.7; design 12.2 Identity "revoke/suspend/deprovision
 * timeline"; 12.3 drill-down; 12.4 accessible timeline — semantic structure, not colour-only).
 *
 * The contract returns the timeline as a page of entries, newest first, each carrying the action
 * (revoke/suspend/reinstate/deprovision/delete/anchor_rotate, plus the synthetic `epoch_change`),
 * the subject agent, who performed it (`actor_type`), the source and event ids, and a `chronology`
 * flag. The region renders them as a semantic ordered table: time, action, agent, actor, chronology.
 *
 * Two honest affordances design calls for:
 * - DRILL-DOWN: each action drills to the lifecycle events behind it (scoped to the agent when the
 *   entry names one), using real `/events` filter params.
 * - CHRONOLOGY: an entry the server marks `indeterminate` (a reporting source outside the accepted
 *   clock-skew bound, FR-4.4) is flagged with an icon+text badge, so the operator knows that entry's
 *   position in the timeline is not reliably ordered — never conveyed by colour alone.
 *
 * The page's `next_cursor` is surfaced as an honest note when more entries exist beyond this page;
 * this screen shows the newest page (a later task can add paging).
 */

export interface LifecycleTimelineProps {
  readonly page: LifecyclePage;
}

type Entry = LifecyclePage["items"][number];

export function LifecycleTimeline(props: LifecycleTimelineProps): ReactNode {
  const navigate = useNavigate();
  const { page } = props;

  const columns: readonly Column<Entry>[] = [
    {
      key: "ts",
      header: "When",
      isRowHeader: true,
      width: "md",
      cell: (r) => (
        <span className="idn-when">
          <time dateTime={isoAttr(r.ts)}>{formatDateTime(r.ts)}</time>
          {r.chronology === "indeterminate" && (
            <StatusBadge
              level="warn"
              label="Order uncertain"
              size="sm"
              title="This entry's reporting source was outside the accepted clock-skew bound, so its position in the timeline is not reliably ordered (FR-4.4)."
            />
          )}
        </span>
      ),
    },
    {
      key: "action",
      header: "Action",
      cell: (r) => (
        <button
          type="button"
          className="idn-link"
          onClick={() => navigate(lifecycleActionHref(r.action, r.agent))}
          aria-label={`Open the ${lifecycleActionLabel(r.action).toLowerCase()} events${r.agent !== null ? ` for agent ${r.agent}` : ""}`}
        >
          {lifecycleActionLabel(r.action)}
        </button>
      ),
    },
    {
      key: "agent",
      header: "Agent",
      cell: (r) => (r.agent === null ? <span className="idn-faint">n/a</span> : <code className="idn-mono">{r.agent}</code>),
    },
    {
      key: "actor",
      header: "By",
      cell: (r) => actorTypeLabel(r.actor_type),
    },
  ];

  return (
    <section className="idn-lifecycle" aria-label="Lifecycle timeline">
      <div className="idn-section__head">
        <h2 className="idn-section__title">Lifecycle timeline</h2>
        <span className="idn-section__note">revoke / suspend / deprovision, newest first</span>
      </div>

      <DenseTable
        caption="Agent lifecycle events, newest first"
        columns={columns}
        rows={page.items}
        rowKey={(r) => r.id}
        stickyHeader
        emptyMessage="No lifecycle events in this window."
      />

      {page.next_cursor !== null && (
        <p className="idn-lifecycle__more">
          Showing the newest events in this window; older events remain beyond this page.
        </p>
      )}
    </section>
  );
}
